import { vocab } from '@/ds';
import { formatNumber, formatTimestamp } from '@/lib/format/format';
import type {
  ActedGroup,
  PreviewGroup,
  PreviewRecord,
  RecommendedRule,
  Rule,
  RuleAction,
  RuleDefinition,
  RuleMode,
  RuleSourceReadiness,
  Suggestion,
} from './api';

// Trust rules in the design system's words.

export const RULE_ACTIONS: RuleAction[] = [
  'Publish',
  'Link',
  'ApplyChanges',
  'DismissUnlikely',
  'RejectOwnerDuplicate',
];

/** The actions the recommendation can add: it never recommends Keep organizers' own events. */
const RECOMMENDABLE_ACTIONS: RuleAction[] = ['Publish', 'Link', 'ApplyChanges', 'DismissUnlikely'];

/** A group action decides an open duplicate group, not a pending record. */
export const isGroupAction = (action: string): boolean =>
  action === 'DismissUnlikely' || action === 'RejectOwnerDuplicate';

export const GROUP_RULE_HINT = 'Group rules use the confidence bar only.';

const plural = (n: number, noun: string) => `${formatNumber(n)} ${noun}${n === 1 ? '' : 's'}`;

/** What a rule of the action covers and decides: a record or a group. */
export const countNoun = (action: string): string => (isGroupAction(action) ? 'group' : 'record');

/** What a rule covers, as a sentence names it: an open group or a pending record. */
export const coveredNoun = (action: string): string =>
  isGroupAction(action) ? 'open group' : 'pending record';

/** A previewed group by its suspected event, else its first record. */
export const previewGroupName = (group: PreviewGroup | undefined): string =>
  group?.suspectedEvent?.name ?? group?.memberNames[0] ?? 'A duplicate group';

/** What a rule's card says it covers now: "Covers 2 open groups now", "Covers no pending record now". */
export function coversWords(action: string, n: number): string {
  if (isGroupAction(action)) {
    return n ? `Covers ${plural(n, 'open group')} now` : 'Covers no open groups now';
  }
  return n ? `Covers ${plural(n, 'pending record')} now` : 'Covers no pending record now';
}

/** The decision itself, as a button says it. */
export const ACTION_VERB: Record<RuleAction, string> = {
  Publish: 'Publish',
  Link: 'Link',
  ApplyChanges: 'Apply changes',
  DismissUnlikely: vocab.GROUP_ACTIONS.DismissUnlikely.suggested,
  RejectOwnerDuplicate: vocab.GROUP_ACTIONS.RejectOwnerDuplicate.suggested,
};

/** What a rule of each action decides, as its group and its choice read. */
export const ACTION_TITLE: Record<RuleAction, string> = {
  Publish: 'Publish new records',
  Link: 'Link to curator events',
  ApplyChanges: 'Apply source changes',
  DismissUnlikely: vocab.GROUP_ACTIONS.DismissUnlikely.title,
  RejectOwnerDuplicate: vocab.GROUP_ACTIONS.RejectOwnerDuplicate.title,
};

export const ACTION_MEANING: Record<RuleAction, string> = {
  Publish: 'A new record, linked to nothing, becomes a live event under the curator organization.',
  Link: 'A record linked to one of the curator’s events, as a Likely match, is approved as that event, which takes its values.',
  ApplyChanges:
    'A published event whose source changed takes the new values, when it’s the curator’s event.',
  DismissUnlikely: vocab.GROUP_ACTIONS.DismissUnlikely.meaning,
  RejectOwnerDuplicate: vocab.GROUP_ACTIONS.RejectOwnerDuplicate.meaning,
};

/** The present tense, for what Act will do: "publishes each pending record". */
const ACTION_DOES: Record<RuleAction, string> = {
  Publish: 'publishes',
  Link: 'links',
  ApplyChanges: 'applies the changes of',
  DismissUnlikely: 'resolves',
  RejectOwnerDuplicate: 'resolves',
};

/** The past tense, for what a rule did: "published 3 records". */
export const ACTION_DONE: Record<RuleAction, string> = {
  Publish: 'published',
  Link: 'linked',
  ApplyChanges: 'applied the changes of',
  DismissUnlikely: 'marked',
  RejectOwnerDuplicate: "kept the organizer's event in",
};

/** The undo the trail offers for each decision. */
export const ACTION_UNDO: Record<RuleAction, string> = {
  Publish: 'Unpublish',
  Link: 'Unlink',
  ApplyChanges: 'Undo changes',
  DismissUnlikely: 'Reopen group',
  RejectOwnerDuplicate: 'Reopen group',
};

export const doesWords = (action: RuleAction) => ACTION_DOES[action];

/** The confirmation's verb: "publish 3 records now". */
export function nowWords(action: RuleAction, n: number): string {
  const records = plural(n, 'record');
  const groups = plural(n, 'group');
  switch (action) {
    case 'ApplyChanges':
      return `apply the changes of ${records} now`;
    case 'Link':
      return `link ${records} now`;
    case 'DismissUnlikely':
      return `mark ${groups} as different events now`;
    case 'RejectOwnerDuplicate':
      return `keep the organizer's event in ${groups} now`;
    default:
      return `publish ${records} now`;
  }
}

/** What Act now did to groups, as the toast says it: the rule's own resolutions, counted from the result. */
export function actedGroupsWords(
  rule: { name: string; action: string },
  acted: ActedGroup[],
): string {
  const done = ACTION_DONE[rule.action as RuleAction];
  const groups = plural(acted.length, 'group');
  const rejected = acted.reduce((sum, g) => sum + g.rejectedCount, 0);
  return rule.action === 'RejectOwnerDuplicate'
    ? `“${rule.name}” ${done} ${groups} and rejected ${plural(rejected, 'candidate')}.`
    : `“${rule.name}” ${done} ${groups} as different events.`;
}

export const modeLevel = (mode: string): 'off' | 'suggest' | 'act' =>
  mode === 'Act' ? 'act' : mode === 'Suggest' ? 'suggest' : 'off';

export const levelMode = (level: string): RuleMode =>
  level === 'act' ? 'Act' : level === 'suggest' ? 'Suggest' : 'Off';

/** The informational warnings a rule may accept, as the API names them, with the design's key. */
export const TOLERABLE_WARNINGS: { kind: string; key: string }[] = [
  { kind: 'NoStartTime', key: 'no-start-time' },
  { kind: 'DateInferred', key: 'date-inferred' },
  { kind: 'NoRaces', key: 'no-races' },
  { kind: 'IncompleteRace', key: 'incomplete-race' },
];

const WARNING_KEYS: Record<string, string> = {
  MissingCity: 'missing-city',
  LowConfidence: 'low-confidence',
  NoStartTime: 'no-start-time',
  DateInferred: 'date-inferred',
  NoRaces: 'no-races',
  IncompleteRace: 'incomplete-race',
  OutOfRegion: 'outside-region',
  SourceChangedAfterEdit: 'source-changed',
  PossiblyCancelled: 'possibly-cancelled',
};

export const warningWord = (kind: string): string =>
  vocab.WARNINGS[WARNING_KEYS[kind] ?? '']?.label ?? kind;

export const warningKey = (kind: string): string | undefined => WARNING_KEYS[kind];

export const percent = (value: number): string => `${Math.round(value * 100)}%`;

/** The warnings clause: "no warnings", "no warnings but No start time". */
function warningsClause(allowed: string[]): string {
  if (allowed.length === 0) return 'no warnings';
  return `no warnings but ${andList(allowed.map(warningWord))}`;
}

export function andList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

export const scopeWords = (sourceName: string | null | undefined): string =>
  sourceName ?? 'all sources';

/** The rule in one line, as TrustLevel labels it: "Publish when confidence ≥ 90% and no warnings · trka.rs". */
export function ruleSentence(definition: RuleDefinition, sourceName: string | null | undefined) {
  const bar = `confidence ≥ ${percent(definition.minConfidence)}`;
  const scope = scopeWords(sourceName);
  if (isGroupAction(definition.action)) {
    return `${ACTION_TITLE[definition.action]} when ${bar} · ${scope}`;
  }
  const verb =
    definition.action === 'Link'
      ? 'Link Likely matches to curator events'
      : definition.action === 'ApplyChanges'
        ? 'Apply source changes to curator events'
        : 'Publish';
  return `${verb} when ${bar} and ${warningsClause(definition.allowedWarnings)} · ${scope}`;
}

export const RECOMMENDED_ACTION = 'Add the recommended rules';
export const RECOMMENDED_NOTHING = 'Nothing to add: the rules already exist';

/** A recommended rule as the rule list reads it, for the confirmation. */
export const recommendedDefinition = (draft: RecommendedRule): RuleDefinition => ({
  action: draft.action as RuleAction,
  sourceId: draft.sourceId,
  minConfidence: draft.minConfidence,
  allowedWarnings: draft.allowedWarnings,
});

/** The actions the recommendation leaves alone: "Link is left as it is: it already has an all-sources rule." */
export function leftAsItIsWords(drafts: RecommendedRule[]): string | null {
  const left = RECOMMENDABLE_ACTIONS.filter((a) => !drafts.some((d) => d.action === a));
  if (left.length === 0) return null;
  const named = left.map((a) => (isGroupAction(a) ? ACTION_TITLE[a] : ACTION_VERB[a]));
  return left.length === 1
    ? `${named[0]} is left as it is: it already has an all-sources rule.`
    : `${andList(named)} are left as they are: each already has an all-sources rule.`;
}

/** What the toast says was added: "Added 2 rules in Suggest: “Publish at 90%” and “Link at 90%”." */
export function addedWords(rules: { name: string }[]): string {
  if (rules.length === 0) return RECOMMENDED_NOTHING;
  return `Added ${formatNumber(rules.length)} rule${rules.length === 1 ? '' : 's'} in Suggest: ${andList(rules.map((r) => `“${r.name}”`))}.`;
}

/** A name to start from, which a person may change. */
export function suggestedName(definition: RuleDefinition, sourceName: string | null | undefined) {
  const base =
    definition.action === 'Link'
      ? 'Link likely matches'
      : definition.action === 'ApplyChanges'
        ? 'Apply source changes'
        : isGroupAction(definition.action)
          ? ACTION_TITLE[definition.action]
          : 'Publish';
  return `${base} at ${percent(definition.minConfidence)}${sourceName ? `, ${sourceName}` : ''}`;
}

/** What a page announces that no rule decides on: a person does. */
export const STATUS_AT_SOURCE = 'Cancelled or postponed at its source';
const UNPUBLISHED_BY_PERSON = 'Removed from the app by a person';

/** What always holds of a group, whatever the bar: the guards no group rule can loosen. */
function alwaysGroupWords(action: RuleAction): string[] {
  const open = 'open, with more than one record or event in it';
  const sources = 'made of records whose sources follow the same rule';
  const undone = 'not a group whose rule decision a person undid';
  return action === 'RejectOwnerDuplicate'
    ? [
        open,
        'not edited by a person',
        'made of pending records only',
        sources,
        undone,
        'suspecting an organizer’s own event, with every record a confirmed match with it and none linked by a person or a rule',
      ]
    : [
        open,
        'not edited by a person, and with no event named by a person',
        sources,
        undone,
        'one where no two items look alike and no two names share a word',
      ];
}

/** The lead of that list: what the rule decides. */
export const alwaysLead = (action: RuleAction): string =>
  isGroupAction(action)
    ? 'Whatever the bar, it decides only a group that is'
    : 'Whatever the bar, it decides only a record that is';

/** What always holds, whatever the bar: the guards no rule can loosen. */
export function alwaysWords(action: RuleAction): string[] {
  if (isGroupAction(action)) return alwaysGroupWords(action);
  const common = [
    'pending, and dated today or later',
    'not edited by a person, and not in an open duplicate group',
    'no Missing city, Outside the region, Possibly cancelled or Source changed',
    'not a record whose rule decision a person undid',
    'not cancelled or postponed at its source',
    'not removed from the app by a person',
  ];
  if (action === 'Link') return [...common, 'linked to the curator’s event, as a Likely match'];
  if (action === 'ApplyChanges') return [...common, 'its event is the curator’s'];
  return [...common, 'linked to no event'];
}

/** A rule's proposal on a record, as the decision bar and the Inbox say it. */
export function suggestionWords(suggestion: Suggestion): string {
  const action = suggestion.action as RuleAction;
  if (action === 'Link' && suggestion.raceEventName) {
    return `Link to “${suggestion.raceEventName}”`;
  }
  return ACTION_VERB[action] ?? suggestion.action;
}

const GROUP_SKIP_WORDS = vocab.GROUP_SKIPS;

/** Why a group rule leaves a group to people, by the API's reason. */
export const groupSkipWords = (reason: string): string => GROUP_SKIP_WORDS[reason] ?? reason;

/** Why a rule leaves a record to people, by the API's first reason. */
export function skipWords(reason: string, warning: string | null, bar: number): string {
  switch (reason) {
    case 'BelowBar':
      return `Confidence below ${percent(bar)}`;
    case 'EditedByPerson':
      return 'Edited by a person, who decides it';
    case 'InDuplicateGroup':
      return 'In a duplicate group';
    case 'BlockingWarning':
    case 'Warning':
      return warning ? warningWord(warning) : 'A warning the rule doesn’t allow';
    case 'DatePassed':
      return 'Its date has passed';
    case 'EventGone':
      return 'Its event has left the app';
    case 'EventNotCurators':
      return 'Its event is an organizer’s, not the curator’s';
    case 'MatchNotLikely':
      return 'Not a Likely match for its event';
    case 'StatusAtSource':
      return STATUS_AT_SOURCE;
    case 'UnpublishedByPerson':
      return UNPUBLISHED_BY_PERSON;
    default:
      return reason;
  }
}

export type Outcome = 'Pending' | 'Waiting' | 'Agreed' | 'Differed' | 'Acted' | 'Expired';

export const CORRECTED_WORD = 'Corrected';
export const CORRECTED_MEANING = `${CORRECTED_WORD} (a factual field changed before approval)`;

/** The fields a person can correct before approving, in the console's words. */
const FIELD_WORDS: Record<string, string> = {
  Date: 'Date',
  StartTime: 'Start time',
  City: 'City',
  Country: 'Country',
  Coordinates: 'Pin on the map',
  Races: 'Races',
};

/** "Date and Start time", as the warnings list reads. */
export const fieldsWords = (fields: string[]): string =>
  andList(fields.map((f) => FIELD_WORDS[f] ?? f));

/** "2 corrected", beside a count of records decided otherwise. */
export const correctedCount = (n: number): string => `${formatNumber(n)} corrected`;

/** The figures the help names where no rule is at hand; cards and dialogs read the API's own values. */
export const READINESS_HELP = {
  windowDays: 90,
  minimumDecisions: 20,
  minimumAgreementPercent: 95,
  calibrationBar: 0.9,
};

type Readiness = Rule['readiness'];

/** "22 of 23 agreed": counts first, never a rounded share. */
export const agreedWords = (agreed: number, decisions: number): string =>
  `${formatNumber(agreed)} of ${formatNumber(decisions)} agreed`;

/** "1 corrected on date or city in 90 days". */
export const correctedOnWords = (n: number, days: number): string =>
  `${correctedCount(n)} on date or city in ${days} days`;

export const NO_CORRECTIONS_ON = 'none corrected on date or city';
export const NO_GOVERNED_SOURCE =
  'Governs no source: every enabled source has its own rule for this action, or none is enabled';

/** The conditions a source can miss, in the words a card and the Act dialog share. */
export function unmetWords(source: RuleSourceReadiness, readiness: Readiness): string {
  switch (source.unmet) {
    case 'TooFewDecisions':
      return `${formatNumber(source.decisions)} of ${formatNumber(readiness.minimumDecisions)} decisions in ${readiness.windowDays} days`;
    case 'TooLittleAgreement':
      return `${agreedWords(source.agreed, source.decisions)}, ${formatNumber(source.agreedNeeded)} needed`;
    case 'CorrectedOnDateOrCity':
      return correctedOnWords(source.correctedOnDateOrCity, readiness.windowDays);
    case 'CalibrationNotYes':
      return source.calibration === 'No'
        ? `confidence at ${formatNumber(readiness.calibrationBar)} doesn't separate corrections`
        : 'calibration not yet answerable';
    default:
      return source.unmet ?? '';
  }
}

export type UnmetLine = {
  ready: false;
  lead: string;
  figure: string;
  calibrationSourceId: string | null;
};

export type ReadinessLine = { ready: boolean; text: string } | UnmetLine;

/** "22 of 23 agreed, none corrected on date or city": a ready source's figures. */
function readyFigures(source: RuleSourceReadiness, group: boolean): string {
  const figures = [agreedWords(source.agreed, source.decisions)];
  if (!group) figures.push(NO_CORRECTIONS_ON);
  return figures.join(', ');
}

/** The condition a source misses: "runtrace.net: 12 of 20 decisions in 90 days". */
const unmetLine = (source: RuleSourceReadiness, readiness: Readiness, before = ''): UnmetLine => ({
  ready: false,
  lead: `${before}${source.sourceName}: `,
  figure: unmetWords(source, readiness),
  calibrationSourceId: source.unmet === 'CalibrationNotYes' ? source.sourceId : null,
});

const READY_HEADS = {
  line: ['Ready for Act', 'Meets the Act conditions'],
  partial: ['Ready on', 'Meets the Act conditions on'],
  source: ['ready', 'meets the Act conditions'],
} as const;

/** How a readiness line starts: it reads as meeting the conditions in Act, ready for Act otherwise. */
const readyHead = (rule: Rule, form: keyof typeof READY_HEADS): string =>
  READY_HEADS[form][rule.mode === 'Act' ? 1 : 0];

/**
 * A rule's readiness for Act as its card says it. Counts lead. In Act it reads as meeting the
 * conditions, and an all-sources rule names its ready sources, then the first one that isn't.
 */
export function readinessLine(rule: Rule): ReadinessLine {
  const { ready, sources, unmet } = rule.readiness;
  const group = isGroupAction(rule.action);
  if (ready) {
    const head = readyHead(rule, 'line');
    const own = rule.sourceId === null ? undefined : sources[0];
    if (!own) return { ready, text: `${head} on ${andList(sources.map((s) => s.sourceName))}` };
    return { ready, text: `${head} — ${readyFigures(own, group)}` };
  }
  const failing = sources.find((s) => s.unmet);
  if (!failing)
    return { ready, text: unmet === 'NoGovernedSource' ? NO_GOVERNED_SOURCE : (unmet ?? '') };
  const readySources = sources.filter((s) => !s.unmet).map((s) => s.sourceName);
  const lead = `${readyHead(rule, 'partial')} ${andList(readySources)} · `;
  return unmetLine(failing, rule.readiness, readySources.length ? lead : '');
}

/** One governed source of an all-sources rule, in the Act dialog's list. */
export function sourceReadinessLine(rule: Rule, source: RuleSourceReadiness): ReadinessLine {
  if (source.unmet) return unmetLine(source, rule.readiness);
  return {
    ready: true,
    text: `${source.sourceName}: ${readyHead(rule, 'source')} — ${readyFigures(source, isGroupAction(rule.action))}`,
  };
}

/**
 * What a rule decided and left in a pass. The Inbox row runs on ("decided 3 on X and left 2 for the
 * next run"); a run's list is punctuated ("decided 3, left 0"), naming the next run only when work is left.
 */
export function ruleFiguresWords(
  { decided, left }: { decided: number; left: number },
  form: 'row' | 'list',
  on?: string,
): string {
  const { RULE_FIGURES: words } = vocab;
  const decidedWords = `${words.decided} ${formatNumber(decided)}`;
  const leftWords = `${words.left} ${formatNumber(left)}`;
  if (form === 'list')
    return `${decidedWords}, ${leftWords}${left > 0 ? ` ${words.forNextRun}` : ''}`;
  return `${decidedWords}${on ? ` ${words.on} ${on}` : ''} ${words.and} ${leftWords} ${words.forNextRun}`;
}

/** The run's link to the audit trail filtered to what rules decided on it. */
export const RULE_DECISIONS_IN_TRAIL = 'Rule decisions in the trail';

/** The link from a run's rule figure to the rule's Decide now dialog. */
export const DECIDE_NOW = 'Decide now…';

/** An Act rule with records waiting decides now unless automation is paused. */
export const canDecideNow = (
  rule: Pick<Rule, 'mode' | 'pendingMatches'>,
  automation: { paused: boolean } | undefined,
): boolean => rule.mode === 'Act' && !automation?.paused && rule.pendingMatches > 0;

export const ACT_STILL_ALLOWED = 'You can still choose Act.';

/** What the Act dialog says Act does: up to the bound after a run or import, the rest next time. */
export function actDescription(
  action: RuleAction,
  bound: number | undefined,
  allSources: boolean,
): string {
  const covered = coveredNoun(action);
  const limit = bound === undefined ? `a set number of ${covered}s` : plural(bound, covered);
  return `After each run or import, the pipeline ${doesWords(action)} up to ${limit} this rule covers and leaves the rest for the next one. The audit trail records each decision with the rule as the actor. Anyone can undo each one (${ACTION_UNDO[action]}) for 7 days.${allSources ? ' Undoing one decision moves the rule back to Suggest for every source.' : ''}`;
}

/** Under "Decides now": it isn't bounded, so the list may be shorter than what it decides. */
export const decidesListedWords = (
  action: string,
  paused: boolean,
  listed: number,
  pending: number,
): string => {
  const decides = paused ? 'would decide' : 'decides';
  return listed < pending
    ? `Listing the first ${formatNumber(listed)}; it ${decides} all ${formatNumber(pending)}.`
    : `It ${decides} every ${countNoun(action)} listed here at once.`;
};

/** Under "Decides now" when the list is empty but records are waiting. */
export const decidesUnlistedWords = (action: string, paused: boolean, pending: number): string =>
  `${paused ? 'It would decide' : 'It decides'} ${plural(pending, countNoun(action))} though none is listed here.`;

/** What a rule would have done to a record as extracted: "published it". */
const ACTION_WOULD_HAVE: Record<RuleAction, string> = {
  Publish: 'published it',
  Link: 'linked it',
  ApplyChanges: 'applied its changes',
  DismissUnlikely: 'dismissed it',
  RejectOwnerDuplicate: "kept the organizer's event",
};

export const OUTCOME_WORDS: Record<Outcome, string> = {
  Pending: 'Waiting',
  Waiting: 'Waiting',
  Agreed: 'Decided the same',
  Differed: 'Decided otherwise',
  Acted: 'Decided by a rule',
  Expired: 'Expired while pending',
};

/** The outcome as the table says it: a differed record a person corrected reads "Corrected". */
export const outcomeWords = (record: PreviewRecord): string =>
  record.outcome === 'Differed' && record.corrected
    ? CORRECTED_WORD
    : (OUTCOME_WORDS[record.outcome as Outcome] ?? record.outcome);

/** A group's outcome as the table says it. */
export const groupOutcomeWords = (group: PreviewGroup): string =>
  OUTCOME_WORDS[group.outcome as Outcome] ?? group.outcome;

/** Still waiting, in a record's or a group's outcome. */
export const isWaiting = (outcome: string): boolean =>
  outcome === 'Pending' || outcome === 'Waiting';

const DECISION_WORDS: Record<string, string> = {
  Published: 'Published',
  Linked: 'Linked',
  ChangesApplied: 'Changes applied',
  Rejected: 'Rejected',
  KeptCurrentVersion: 'Kept the published version',
  DuplicatesResolved: 'Rejected as a duplicate',
};

/**
 * What happened to a record the rule covers: "Rejected by ana · 25 Sep: Not a race", and when a
 * person corrected facts first, what the rule would have done with the record as extracted.
 */
export function happenedWords(record: PreviewRecord, action: RuleAction, now = new Date()): string {
  const decided = decisionWords(record, now);
  if (!record.corrected && record.correctedFields.length === 0) return decided;
  const fields = record.correctedFields.length
    ? fieldsWords(record.correctedFields)
    : 'a factual field';
  return record.corrected
    ? `${decided}. The rule would have ${ACTION_WOULD_HAVE[action]} as extracted while a person corrected ${fields} first`
    : `${decided}. A person corrected ${fields} first`;
}

function decisionWords(record: PreviewRecord, now: Date): string {
  if (record.ruleDecisionUndone) return 'A person undid a rule’s decision on it';
  if (record.outcome === 'Pending') return 'Still pending';
  if (record.outcome === 'Expired') return 'Its date passed while pending';
  const d = record.decision;
  if (!d) {
    if (record.status !== 'Approved') return 'Decided';
    return record.recordDecision === 'Linked'
      ? 'Linked before the audit trail'
      : 'Published before the audit trail';
  }
  const words = DECISION_WORDS[d.action] ?? d.action;
  return `${words} by ${actorWords(d)} · ${formatTimestamp(d.at, now)}${d.reason && d.action !== 'Published' ? `: ${d.reason}` : ''}`;
}

function actorWords(d: NonNullable<PreviewRecord['decision']>): string {
  return d.actorKind === 'Person'
    ? (d.actorName ?? 'someone')
    : d.actorKind === 'Rule'
      ? `the rule “${d.actorName}”`
      : 'the pipeline';
}

const { NotDuplicates, KeepExistingEvent, KeepStaged } = vocab.GROUP_RESOLUTIONS;
const GROUP_RESOLUTION_WORDS: Record<string, string> = {
  NotDuplicates: NotDuplicates.trail,
  KeepExistingEvent: KeepExistingEvent.trail,
  KeepStaged: `${KeepStaged.trail} ${KeepStaged.unnamed}`,
};

/** What happened to a group the rule covers: "Still open", or "Different events by ana · 25 Sep: reason". */
export function groupHappenedWords(group: PreviewGroup, now = new Date()): string {
  if (group.ruleDecisionUndone) return 'A person undid a rule’s decision on it';
  if (group.outcome === 'Waiting') return 'Still open';
  const d = group.decision;
  if (!d) return 'Resolved';
  const words = (group.resolution && GROUP_RESOLUTION_WORDS[group.resolution]) || 'Resolved';
  return `${words} by ${actorWords(d)} · ${formatTimestamp(d.at, now)}${d.reason ? `: ${d.reason}` : ''}`;
}

/** "Off since 25 Sep, 14:02 by ana". */
export function modeSince(rule: Rule, now = new Date()): string {
  return `${rule.mode} since ${formatTimestamp(rule.updatedAt, now)}${rule.updatedByName ? ` by ${rule.updatedByName}` : ''}`;
}

export const WINDOWS = [7, 30, 90] as const;
